// Global
import React from 'react';
import clsx from 'clsx';

// Local
import styles from '../Card.module.scss';
import listViewStyles from './ListViewRow.module.scss';
import { ContentGridCardProps } from './ContentGrid';
import { SearchRecord } from 'lib/services/models/search-response';
import EnhancedRichText from 'components/helpers/Atoms/EnhancedRichText/EnhancedRichText';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import StarRating from 'components/helpers/StarRating/StarRating';
import useDictionary from 'lib/hooks/useDictionary';
import { generateStarRating } from 'lib/utils/generate-rating';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { CardImage, CardLabelAppearance, CardLabelColor } from '../__helpers__/CardImageWrapper';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { ContentOverride, CardFields, normalizeStandardImageUrl } from '../CardListingShared';
import { formatDate } from 'lib/utils/date-formatter';
import { SaveButton } from 'components/helpers/ActionToolbar/ActionButtons/SaveButton/SaveButton';
import { isSitecoreItemPath } from 'lib/utils/components/content-list-data';
import { useContentQuery } from './ContentQueryContext';
import { useFacetConfig } from './FacetConfigContext';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { useMetaDataTag } from 'lib/hooks/useMetaDataTag';
import { pushSearchResultEvent } from 'components/helpers/Analytics/SearchAndFilters/SearchAndFiltersAnalytics';
import { normalizeSymbols } from 'lib/utils/normalize-symbols';
import {
  isContentGridContentResultEnabled,
  pushContentGridContentResultEvent,
} from 'components/helpers/Analytics/ContentGrid/ContentGridAnalytics';

// Shared search-result-click analytics handler, reused by both the card grid
// (ContentGridCard) and the list view (ListViewRow) rendering modes.
export function useContentResultClickHandler({
  card,
  contentOverride,
  searchAnalyticsEnabled,
  searchType,
  contentResultAnalytics,
}: {
  card: SearchRecord;
  contentOverride?: ContentOverride;
  searchAnalyticsEnabled?: ContentGridCardProps['searchAnalyticsEnabled'];
  searchType?: ContentGridCardProps['searchType'];
  contentResultAnalytics?: ContentGridCardProps['contentResultAnalytics'];
}) {
  const { query, searchResponse } = useContentQuery();
  const { facetConfig } = useFacetConfig();
  const { locale } = useGlobalData();
  const { sitecoreContext } = useSitecoreContext();

  return () => {
    const contentTitle = contentOverride?.title || card.Title || '';
    const currentPageType =
      (sitecoreContext?.contentItem as { ContentType?: string } | undefined)?.ContentType ||
      (sitecoreContext?.itemPath === '/' ? 'Home' : 'Other');
    const currentLanguage = (sitecoreContext?.language || locale || '').toLowerCase();

    if (isContentGridContentResultEnabled({ analyticsConfig: contentResultAnalytics })) {
      pushContentGridContentResultEvent({
        analyticsConfig: contentResultAnalytics!,
        card,
        contentTitle,
        currentPageType,
        language: currentLanguage,
      });
    }

    if (!searchAnalyticsEnabled) {
      return;
    }

    const selectedFacetEntries = (query?.facetGroups ?? []).flatMap((group) => {
      const configuredGroup = facetConfig.facetGroup.find((item) => item.groupName === group.name);
      const facetFilterLabel = configuredGroup?.groupLabel ?? group.name;

      return (group.facets ?? []).map((facet) => ({
        facetFilter: facetFilterLabel,
        facetFilterValue: facet.name,
      }));
    });

    const normalizedSortType =
      query?.sortBy && query.sortBy !== 'None' ? query.sortBy : 'Best Match';

    pushSearchResultEvent({
      searchType: searchType ?? 'Standard',
      contentTitle,
      language: locale?.split('-')[0]?.toLowerCase() || 'en',
      facetFilter: selectedFacetEntries.map((entry) => entry.facetFilter),
      facetFilterValue: selectedFacetEntries.map((entry) => entry.facetFilterValue),
      recordType: card.RecordType || card.ContentType,
      contentType: card.ContentType,
      searchSortType: normalizedSortType,
      siteSearchPhrase: query?.searchTerm ?? '',
      labelBadge: contentOverride?.tag ?? '',
      pagination: String(query?.pageIndex ?? searchResponse?.PageIndex ?? 1),
      filteredResult: selectedFacetEntries.length > 0 ? 'yes' : 'no',
      productName: card.ContentType === 'Product' ? card.Title || '' : '',
      productId: card.ContentType === 'Product' ? card.ContentId || '' : '',
      searchResultCount: String(searchResponse?.Count ?? 0),
    });
  };
}

export const ContentGridCard = (props: ContentGridCardProps) => {
  const {
    card,
    contentOverride,
    fieldsToShow,
    gridAspectRatio = '3:2',
    aspectRatio = '3:2',
    forceWhiteBackground,
    additionalCssClassName,
    alignment = 'LeftAlign',
    showLabel,
    showManageFavorites,
  } = props;
  const { locale } = useGlobalData();
  const {
    labelName: metaDataTagLabelName,
    metaDataTagAppearance,
    metaDataTagColor,
    defaultMetaDataTagAppearance,
    defaultMetaDataTagColor,
  } = useMetaDataTag(card.MetadataIds);
  const hideLink = !!contentOverride?.hideLink;
  const hasLink = !hideLink && !!(contentOverride?.url || card.ItemUrl);
  const cardTitleForAlt = (contentOverride?.title || card.Title || '').trim();
  const { sitecoreContext } = useSitecoreContext();
  const handleSearchResultClick = useContentResultClickHandler({
    card,
    contentOverride,
    searchAnalyticsEnabled: props.searchAnalyticsEnabled,
    searchType: props.searchType,
    contentResultAnalytics: props.contentResultAnalytics,
  });

  return (
    <div
      className={clsx(
        styles.card,
        additionalCssClassName,
        {
          [styles.card_Center]: alignment === 'CenterAlign',
        },
        { [styles.nonInteractive]: !hasLink }
      )}
      key={card.ContentId}
    >
      <div className={clsx(styles.cardContent)}>
        <CardImageWrapper
          fieldsToShow={fieldsToShow}
          card={card}
          contentOverride={contentOverride}
          fallbackImageUrl={props.fallbackImageUrl}
          gridAspectRatio={gridAspectRatio}
          aspectRatio={aspectRatio}
          forceWhiteBackground={forceWhiteBackground}
          showLabel={showLabel}
          labelAppearance={
            (contentOverride?.tag
              ? defaultMetaDataTagAppearance
              : metaDataTagAppearance) as CardLabelAppearance
          }
          labelColor={
            (contentOverride?.tag ? defaultMetaDataTagColor : metaDataTagColor) as CardLabelColor
          }
          labelName={
            fieldsToShow.includes('ImageLabel')
              ? contentOverride?.tag || metaDataTagLabelName
              : undefined
          }
          imageAltText={cardTitleForAlt}
        />
        <div
          className={clsx(styles.cardBody, {
            [styles.cardBody_manageFavorites]: showManageFavorites,
          })}
        >
          <div
            className={clsx(styles.cardBodyCopy, {
              [styles.cardBodyCopy_manageFavorites]: showManageFavorites,
            })}
          >
            <Eyebrow fieldsToShow={fieldsToShow} card={card} />
            <div className={clsx(styles.cardBodyTitleWrapper)}>
              <div className={clsx(styles.cardBodyTitleBlock)}>
                <Title
                  fieldsToShow={fieldsToShow}
                  url={contentOverride?.url || card.ItemUrl}
                  title={contentOverride?.title || card.Title}
                  contentType={card.ContentType}
                  locale={sitecoreContext?.language || locale}
                  tag={
                    fieldsToShow.includes('ImageLabel')
                      ? contentOverride?.tag || metaDataTagLabelName
                      : undefined
                  }
                  onClick={handleSearchResultClick}
                  disableLink={hideLink}
                  releaseDate={contentOverride?.releaseDate}
                />
                <ProductNumber fieldsToShow={fieldsToShow} card={card} />
              </div>
              <RatingsAndKitchenTested fieldsToShow={fieldsToShow} card={card} />
              <RecipeFastFactsList
                fieldsToShow={fieldsToShow}
                card={card}
                singleColumn={showManageFavorites}
              />
            </div>
            <Description
              fieldsToShow={fieldsToShow}
              description={contentOverride?.description || card.Description || ''}
              hidden={showManageFavorites}
            />
            <AddToFavorites
              fieldsToShow={fieldsToShow}
              card={card}
              showManageButton={props.showManageFavorites}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function CardImageWrapper(props: {
  fieldsToShow: CardFields[];
  card: SearchRecord;
  contentOverride?: ContentOverride;
  fallbackImageUrl?: string;
  gridAspectRatio: ImageAspectRatio;
  aspectRatio: ImageAspectRatio;
  forceWhiteBackground?: boolean;
  showLabel?: boolean;
  labelAppearance?: CardLabelAppearance;
  labelColor?: CardLabelColor;
  labelName?: string;
  imageAltText?: string;
}) {
  const {
    fieldsToShow,
    card,
    contentOverride,
    fallbackImageUrl,
    gridAspectRatio,
    aspectRatio,
    forceWhiteBackground,
    showLabel,
    labelAppearance = 'Fill',
    labelColor = 'Type A',
    labelName,
    imageAltText,
  } = props;
  const imageSrc =
    normalizeStandardImageUrl(contentOverride?.image, card?.PageUrl) ||
    normalizeStandardImageUrl(card?.StandardImageUrl, card?.PageUrl) ||
    normalizeStandardImageUrl(fallbackImageUrl, card?.PageUrl);

  if (!fieldsToShow.includes('Image') || !imageSrc) {
    return null;
  }

  return (
    <CardImage
      imageSrc={imageSrc}
      labelName={labelName}
      labelAppearance={labelAppearance}
      labelColor={labelColor}
      showLabel={showLabel}
      gridAspectRatio={gridAspectRatio}
      aspectRatio={aspectRatio}
      forceWhiteBackground={forceWhiteBackground}
      imageAltText={imageAltText}
    />
  );
}

function Eyebrow(props: { fieldsToShow: CardFields[]; card: SearchRecord }) {
  const { fieldsToShow, card } = props;
  if (!fieldsToShow.includes('Eyebrow')) {
    return null;
  }
  return <div className={clsx(styles.cardBodyEyebrow)}>{card.ContentType}</div>;
}

function isLocaleRelativeLink(href: string): boolean {
  return /^[a-z]{2}-[a-z]{2}(?=\/|$)/i.test(href);
}

function collapseAnyDuplicateLocalePrefix(path: string): string {
  return path.replace(/^\/([a-z]{2}-[a-z]{2})(?:\/\1)+(?=\/|$)/i, '/$1');
}

function collapseDuplicateLocalePrefix(path: string, locale: string): string {
  const normalizedLocale = locale.toLowerCase();
  const duplicateLocalePattern = new RegExp(
    `^/(?:${normalizedLocale})(?:/${normalizedLocale})+(?=/|$)`,
    'i'
  );

  return path.replace(duplicateLocalePattern, `/${normalizedLocale}`);
}

function collapseCardLocalePrefixes(path: string, locale?: string): string {
  const localeCollapsed = locale ? collapseDuplicateLocalePrefix(path, locale) : path;
  return collapseAnyDuplicateLocalePrefix(localeCollapsed);
}

export function normalizeCardUrl(url: string, locale?: string): string {
  const trimmedUrl = url.trim();

  if (!trimmedUrl || trimmedUrl.startsWith('/api/')) {
    return url;
  }

  if (isLocaleRelativeLink(trimmedUrl)) {
    return collapseCardLocalePrefixes(`/${trimmedUrl}`, locale);
  }

  if (trimmedUrl.startsWith('/')) {
    return collapseCardLocalePrefixes(trimmedUrl, locale);
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    const normalizedPath = collapseCardLocalePrefixes(
      `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
      locale
    );

    if (normalizedPath !== `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`) {
      return locale ? normalizedPath : `${parsedUrl.origin}${normalizedPath}`;
    }
  } catch {
    return url;
  }

  return url;
}

export function Title(props: {
  fieldsToShow: CardFields[];
  url: string;
  title: string;
  contentType: string;
  tag: string | undefined;
  locale?: string;
  onClick?: () => void;
  disableLink?: boolean;
  releaseDate?: string;
  variant?: 'card' | 'list';
}) {
  const {
    fieldsToShow,
    url,
    title,
    contentType,
    tag,
    locale,
    onClick,
    disableLink,
    releaseDate,
    variant = 'card',
  } = props;
  if (!fieldsToShow.includes('Title')) {
    return null;
  }
  const titleWrapperClass =
    variant === 'list' ? listViewStyles.listViewRowTitle : styles.cardBodyTitle;
  const titleLinkClass =
    variant === 'list' ? listViewStyles.listViewRowTitleLink : styles.cardBodyTitleFullCardLink;
  const normalizedTitle = normalizeSymbols(title || '');
  const ariaLabel = tag ? `${tag} ${normalizedTitle}` : `${normalizedTitle}`;
  // When the link is disabled (e.g. a timed release override that has not been
  // released yet), keep the card non-clickable so the underlying content stays
  // hidden, but still expose it to keyboard/AT users with the same focus state
  // and aria label as an interactive card. The accessible label is built from
  // the (override) teaser content passed in via `title`/`tag`, never the
  // unrevealed card data. Once released, the card falls through to the
  // interactive branch below and links to the revealed content.
  if (disableLink) {
    // Announce to assistive technology that this is a hidden timed-release card
    // and, when known, the date it becomes available. The status is exposed as
    // real visually-hidden text so it becomes part of the focusable element's
    // accessible name. That focusable element is given role="link" +
    // aria-disabled below so NVDA (which only derives a name from content for
    // elements with a supporting role) announces it, matching VoiceOver/TalkBack.
    const releaseDateText = formatDate(releaseDate, locale, 'LONG');
    const hiddenAnnouncement = releaseDateText
      ? `Hidden content, available ${releaseDateText}`
      : 'Hidden content, coming soon';
    return (
      <h3 className={clsx(titleWrapperClass)}>
        <span
          className={clsx(titleLinkClass)}
          // A generic <span> with tabIndex but no role does NOT derive an
          // accessible name from its child text (per the ARIA "name from
          // content" rules). VoiceOver/TalkBack read it anyway, but NVDA on
          // Windows follows the spec strictly and announces nothing. Exposing
          // it as a (disabled) link gives the focusable element a role that
          // supports name-from-content, so the title + visually-hidden status
          // are reliably announced across NVDA, VoiceOver and TalkBack.
          role="link"
          aria-disabled="true"
          tabIndex={0}
        >
          {tag && <span className={clsx(styles.srOnly)}>{`${tag} `}</span>}
          {normalizedTitle}
          <span className={clsx(styles.srOnly)}>{`, ${hiddenAnnouncement}`}</span>
        </span>
      </h3>
    );
  }
  // We need to add the /recipes prefix to the url for recipes since RMS can't provide that.
  // However, we only want to do this if the url is a relative path. If the url is an absolute path (e.g. external link), we assume it's correct and don't want to modify it.
  const normalizedUrl = normalizeCardUrl(url, locale);
  const realUrl =
    contentType === 'Recipe' &&
    !(normalizedUrl.includes('https://') || normalizedUrl.includes('http://'))
      ? `/recipes/${normalizedUrl}`
      : normalizedUrl;
  // Guard: never use raw Sitecore item paths (e.g. /Components/...) as hrefs.
  // If the URL resolves to a datasource-only path there is nothing to link to.
  const safeUrl = isSitecoreItemPath(realUrl) ? '' : realUrl;
  return (
    <h3 className={clsx(titleWrapperClass)}>
      <CtaLink
        url={safeUrl}
        className={clsx(titleLinkClass)}
        ctaStyle="none"
        text={normalizedTitle}
        aria-label={ariaLabel}
        onClick={onClick}
      />
    </h3>
  );
}

function ProductNumber(props: { fieldsToShow: CardFields[]; card: SearchRecord }) {
  const { fieldsToShow, card } = props;
  if (!fieldsToShow.includes('ProductNumber') || !card.ProductId) {
    return null;
  }
  const truncatedProductId = parseInt(card.ProductId, 10).toString();
  return <p className={clsx(styles.cardBodyProductNumber)}>{truncatedProductId}</p>;
}

function RatingsAndKitchenTested(props: { fieldsToShow: CardFields[]; card: SearchRecord }) {
  const { fieldsToShow, card } = props;
  const { getDictionaryValue } = useDictionary();

  if (card.ContentType !== 'Recipe' && card.ContentType !== 'Product') {
    return null;
  }
  const showRating = fieldsToShow.includes('Rating');
  const showKitchenTested =
    fieldsToShow.includes('KitchenTested') && card.ContentType === 'Recipe' && card.KitchenTested;

  const show = showRating || showKitchenTested;
  if (!show) {
    return null;
  }

  return (
    <div className={clsx(styles.cardBodyRatingsAndKitchenTested)}>
      <RatingStars fieldsToShow={fieldsToShow} card={card} />
      {showKitchenTested && (
        <div className={clsx(styles.recipeKitchenTestedTagWraper)}>
          <SvgIcon
            className={clsx(styles.recipeKitchenTestedTagIcon)}
            icon="kitchentips"
            size="xs"
          />
          <span className={clsx(styles.recipeKitchenTestedTagText)}>
            {getDictionaryValue('Components.Recipe.RecipeHero.KitchenTested') || 'Kitchen Tested'}
          </span>
        </div>
      )}
    </div>
  );
}

function RatingStars(props: { fieldsToShow: CardFields[]; card: SearchRecord }) {
  const { fieldsToShow, card } = props;

  const { getDictionaryValue } = useDictionary();
  if (!fieldsToShow.includes('Rating')) {
    return null;
  }

  const starRating = generateStarRating(card.AverageRating);

  const ratingText = getDictionaryValue(
    'Common.Cards.RatingsScreenReaderText',
    '{stars} out of 5 stars, average rating value out of {ratingCount} reviews.',
    [
      { key: '{stars}', value: (card.AverageRating * 5).toPrecision(2) },
      { key: '{ratingCount}', value: card.RatingCount.toString() },
    ]
  );

  return (
    <div className={clsx(styles.cardBodyRatings)}>
      <span className="screenReaderText">{ratingText}</span>

      <span aria-hidden className={clsx(styles.ratingStarsWrapper)}>
        {starRating.map((starType, index) => (
          <StarRating
            key={index}
            id={`${index.toString()}-${card.ContentId}`}
            className={clsx(styles.ratingStar)}
            percentage={starType}
          />
        ))}
      </span>
      <span aria-hidden className={clsx(styles.productCardRatingText)}>
        ({card.RatingCount})
      </span>
    </div>
  );
}

function RecipeFastFactsList(props: {
  fieldsToShow: CardFields[];
  card: SearchRecord;
  singleColumn?: boolean;
}) {
  const { fieldsToShow, card, singleColumn } = props;
  const { getDictionaryValue } = useDictionary();

  if (card.ContentType !== 'Recipe') {
    return null;
  }
  let show = false;
  if (fieldsToShow.includes('PrepTime') && card.PrepTime) {
    show = true;
  }
  if (fieldsToShow.includes('TotalTime') && card.TotalTime) {
    show = true;
  }
  if (fieldsToShow.includes('Ingredients') && card.NumberOfIngredients) {
    show = true;
  }
  if (fieldsToShow.includes('Servings') && card.ServingNumber) {
    show = true;
  }
  if (!show) {
    return null;
  }
  return (
    <ul
      className={clsx(styles.cardBodyFastFacts, { [styles.cardBodyFastFactsColumn]: singleColumn })}
    >
      <RecipeFastFact
        fieldsToShow={fieldsToShow}
        label={getDictionaryValue('Components.Recipe.Attributes.PrepTime') || 'Prep Time'}
        field="PrepTime"
        value={card.PrepTime}
      />
      <RecipeFastFact
        fieldsToShow={fieldsToShow}
        label={getDictionaryValue('Components.Recipe.Attributes.TotalTime') || 'Total Time'}
        field="TotalTime"
        value={card.TotalTime}
      />
      <RecipeFastFact
        fieldsToShow={fieldsToShow}
        label={getDictionaryValue('Components.Recipe.Attributes.Ingredients') || 'Ingredients'}
        field="Ingredients"
        value={card.NumberOfIngredients}
      />
      <RecipeFastFact
        fieldsToShow={fieldsToShow}
        label={getDictionaryValue('Components.Recipe.Attributes.Servings') || 'Servings'}
        field="Servings"
        value={card.ServingNumber}
      />
    </ul>
  );
}

type FastFact = 'PrepTime' | 'TotalTime' | 'Ingredients' | 'Servings';

function RecipeFastFact(props: {
  fieldsToShow: CardFields[];
  field: FastFact;
  label: string;
  value: string;
}) {
  const { fieldsToShow, label, field, value } = props;
  if (!fieldsToShow.includes(field) || !value) {
    return null;
  }
  return (
    <li>
      <span className={clsx(styles.recipeFastFact)}>
        <strong>{label}</strong>: {value}
      </span>
    </li>
  );
}

export function Description(props: {
  fieldsToShow: CardFields[];
  description: string;
  hidden?: boolean;
  variant?: 'card' | 'list';
}) {
  const { fieldsToShow, description, hidden, variant = 'card' } = props;
  if (hidden || !fieldsToShow.includes('Description')) {
    return null;
  }
  const descriptionClass =
    variant === 'list' ? listViewStyles.listViewRowDescription : styles.cardBodyDescription;
  return (
    <div className={clsx(descriptionClass)}>
      <EnhancedRichText value={description} />
    </div>
  );
}
function AddToFavorites(props: {
  fieldsToShow: CardFields[];
  card: SearchRecord;
  showManageButton?: boolean;
}) {
  const { fieldsToShow, card, showManageButton } = props;

  if (!showManageButton && !fieldsToShow.includes('Favorite')) {
    return null;
  }

  return (
    <div
      className={clsx(styles.cardBodyCtaWrapper, {
        [styles.cardBodyCtaWrapper_manageFavorites]: showManageButton,
      })}
    >
      <SaveButton variant="card" contentItem={card} />
    </div>
  );
}
